/**
 * Hook para gerenciar dados do estabelecimento público
 */

import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import type { EstablishmentPublic } from "../types";
import { getPublicEstablishment } from "../../../services/public";
import { useFeedbackState } from "../../../components/ui/ToastProvider";

// Assinatura inativa ou unidade desativada têm tela própria nas páginas, mesmo
// que algum dia cheguem como 404: não contam como salão inexistente.
const PUBLIC_ACCESS_ERROR =
  /subscription_required|assinatura\s+est[aá]\s+inativa|establishment_deactivated|unidade\s+est[aá]\s+desativada/i;

export function usePublicEstablishment() {
  const params = useParams();
  const slugParam = (params as Record<string, string | string[]>)?.slug;
  const slug = Array.isArray(slugParam) ? slugParam[0] : slugParam;

  const [establishment, setEstablishment] = useState<EstablishmentPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useFeedbackState<string | null>(null);
  // Salão inexistente (404). Fica fora do `error` para não virar toast: as
  // páginas mostram um estado vazio único (QA de 06/10/2026, L4).
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug || typeof slug !== "string") return;

    setLoading(true);
    setError(null);
    setNotFound(false);

    getPublicEstablishment(slug)
      .then((data) => setEstablishment(data))
      .catch((err) => {
        const message = err?.message || "Erro ao carregar estabelecimento";
        if (err?.status === 404 && !PUBLIC_ACCESS_ERROR.test(String(message))) {
          setNotFound(true);
          return;
        }
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return {
    establishment,
    loading,
    error,
    notFound,
    slug: typeof slug === "string" ? slug : null,
  };
}
